import React, { useState, useEffect } from 'react';
import { Package, TrendingUp, Eye, Download, Edit, Trash2, X, Save, User, Mail, Euro } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { supabase } from '../lib/supabase';
import { paymentsService, PaymentSettings } from '../services/paymentsService';

interface InvoiceConcept {
  type: 'subscription' | 'advertising' | 'sales_commission' | 'artwork_sale';
  description: string;
  duration?: string;
  percentage?: number;
  base_amount: number;
  vat_rate?: number;
  vat_amount: number;
  total_amount: number;
}

interface Order {
  id: string;
  order_number: string;
  seller_id: string;
  buyer_id?: string;
  buyer_email?: string;
  buyer_name?: string;
  post_id: string;
  original_price: number;
  final_price: number;
  commission_rate: number;
  commission_amount: number;
  artist_earnings: number;
  delivery_method: 'digital' | 'physical' | 'both';
  delivery_address?: Record<string, unknown>;
  status: 'pending' | 'confirmed' | 'processing' | 'shipped' | 'delivered' | 'cancelled' | 'refunded';
  payment_status: 'pending' | 'paid' | 'partially_paid' | 'refunded' | 'failed';
  buyer_notes?: string;
  seller_notes?: string;
  tracking_number?: string;
  shipped_at?: string;
  delivered_at?: string;
  created_at: string;
  updated_at: string;
  vat_rate?: number;
  is_intracommunity?: boolean;
  vies_verified?: boolean;
  tax_exemption_reason?: string;
  invoice_concepts?: InvoiceConcept[];
  holded_invoice_id?: string;
  posts?: Record<string, unknown>;
  buyer_profile?: Record<string, unknown>;
}

const SalesView: React.FC = () => {
  const { user } = useAuth();
  const { select, locale } = useLanguage();

  const statusLabels: Record<string, { es: string; en: string }> = {
    pending: { es: 'Pendiente', en: 'Pending' },
    confirmed: { es: 'Confirmado', en: 'Confirmed' },
    processing: { es: 'Procesando', en: 'Processing' },
    shipped: { es: 'Enviado', en: 'Shipped' },
    delivered: { es: 'Entregado', en: 'Delivered' },
    cancelled: { es: 'Cancelado', en: 'Cancelled' },
    refunded: { es: 'Reembolsado', en: 'Refunded' },
  };

  const paymentStatusLabels: Record<string, { es: string; en: string }> = {
    pending: { es: 'Pendiente', en: 'Pending' },
    paid: { es: 'Pagado', en: 'Paid' },
    partially_paid: { es: 'Pagado parcial', en: 'Partially paid' },
    refunded: { es: 'Reembolsado', en: 'Refunded' },
    failed: { es: 'Fallido', en: 'Failed' },
  };

  const deliveryMethodLabels: Record<Order['delivery_method'], { es: string; en: string }> = {
    digital: { es: 'Digital', en: 'Digital' },
    physical: { es: 'Físico', en: 'Physical' },
    both: { es: 'Ambos', en: 'Both' },
  };

  const getStatusLabel = (status: string) => select(statusLabels[status] || { es: status, en: status });
  const getPaymentStatusLabel = (status: string) => select(paymentStatusLabels[status] || { es: status, en: status });
  const getDeliveryMethodLabel = (method: Order['delivery_method']) => select(deliveryMethodLabels[method] || { es: method, en: method });
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [paymentSettings, setPaymentSettings] = useState<PaymentSettings | null>(null);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState<Partial<Order>>({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    void loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError('');
      
      const { data, error: ordersError } = await supabase
        .from('orders')
        .select(`
          *,
          posts (
            id,
            title,
            description,
            media_files (
              url,
              type
            )
          ),
          buyer_profile:profiles!orders_buyer_id_fkey (
            id,
            username,
            full_name,
            avatar_url
          )
        `)
        .eq('seller_id', user?.id)
        .order('created_at', { ascending: false });

      if (ordersError) throw ordersError;
      setOrders(data || []);

      const settings = await paymentsService.getPaymentSettings();
      setPaymentSettings(settings);
    } catch (err) {
      setError(select({ es: 'Error al cargar los pedidos', en: 'Failed to load orders' }));
      console.error('Error loading orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewOrderDetails = (order: Order) => {
    console.log('Viewing order details:', order);
    setSelectedOrder(order);
    setShowDetailsModal(true);
  };

  const validateInvoiceForOrder = (order: Order): string | null => {
    const missing: string[] = [];

    if (!order.buyer_name && !order.buyer_profile?.full_name) {
      missing.push(select({ es: 'nombre del comprador', en: 'buyer name' }));
    }

    if (!order.buyer_email) {
      missing.push(select({ es: 'email del comprador', en: 'buyer email' }));
    }

    if (!order.order_number) {
      missing.push(select({ es: 'número de factura/pedido', en: 'invoice/order number' }));
    }

    if (!order.created_at) {
      missing.push(select({ es: 'fecha del pedido', en: 'order date' }));
    }

    if (!order.final_price || order.final_price <= 0) {
      missing.push(select({ es: 'importe de la venta', en: 'sale amount' }));
    }

    if (order.is_intracommunity && !order.vies_verified) {
      return select({
        es: 'No se puede emitir la factura automáticamente porque el estado VIES del comprador no está verificado. Revisa la verificación intracomunitaria antes de continuar.',
        en: "The invoice cannot be generated automatically because the buyer's VIES status is not verified. Please confirm intra-Community VAT registration before continuing.",
      });
    }

    if (missing.length > 0) {
      return select({
        es: `Faltan datos obligatorios para emitir la factura: ${missing.join(', ')}.`,
        en: `Required fields are missing to issue the invoice: ${missing.join(', ')}.`,
      });
    }

    return null;
  };

  const handleDownloadInvoice = (order: Order) => {
    console.log('Downloading invoice for order:', order.order_number);
    
    try {
      const validationMessage = validateInvoiceForOrder(order);
      if (validationMessage) {
        alert(validationMessage);
        return;
      }

      // Generate invoice content
      const invoiceContent = generateInvoiceContent(order);
      
      // Create and download file
      const blob = new Blob([invoiceContent], { type: 'text/plain;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const invoiceFileName = select({ es: 'factura', en: 'invoice' });
      link.download = `${invoiceFileName}-${order.order_number}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('Invoice downloaded successfully');
    } catch (err) {
      console.error('Error downloading invoice:', err);
      alert(select({ es: 'Error al descargar la factura', en: 'Error downloading invoice' }));
    }
  };

  const generateInvoiceContent = (order: Order): string => {
    const now = new Date();
    const orderDate = new Date(order.created_at);
    const formatCurrency = (value: number) =>
      new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(value);
    const defaultVatRate = typeof order.vat_rate === 'number'
      ? order.vat_rate
      : paymentSettings?.tax_rate ?? 0;
    const isIntracommunity = order.is_intracommunity;
    const viesVerified = order.vies_verified;
    const effectiveVatRate = isIntracommunity && viesVerified ? 0 : defaultVatRate;
    const baseFromGross = (gross: number, rate: number) => {
      if (!rate) return gross;
      return gross / (1 + rate / 100);
    };
    const conceptTypeLabels: Record<InvoiceConcept['type'], { es: string; en: string }> = {
      subscription: { es: 'Suscripción premium', en: 'Premium subscription' },
      advertising: { es: 'Publicidad', en: 'Advertising' },
      sales_commission: { es: 'Comisión por ventas', en: 'Sales commission' },
      artwork_sale: { es: 'Venta de obra', en: 'Artwork sale' },
    };
    const getConceptTypeLabel = (type: InvoiceConcept['type']) =>
      select(conceptTypeLabels[type] || { es: type, en: type });
    const concepts: InvoiceConcept[] = order.invoice_concepts && order.invoice_concepts.length > 0
      ? order.invoice_concepts.map((concept) => {
          const rate = typeof concept.vat_rate === 'number' ? concept.vat_rate : effectiveVatRate;
          const gross = concept.total_amount || concept.base_amount;
          const base = baseFromGross(gross, rate);
          const vatAmount = rate ? gross - base : 0;
          return {
            ...concept,
            vat_rate: rate,
            base_amount: base,
            vat_amount: vatAmount,
            total_amount: gross,
          };
        })
      : [
          {
            type: 'artwork_sale',
            description: `${select({ es: 'Venta de obra de arte', en: 'Artwork sale' })} - ${
              order.posts?.title || select({ es: 'Obra sin título', en: 'Untitled piece' })
            }`,
            base_amount: baseFromGross(order.final_price, effectiveVatRate),
            vat_rate: effectiveVatRate,
            vat_amount: effectiveVatRate
              ? order.final_price - baseFromGross(order.final_price, effectiveVatRate)
              : 0,
            total_amount: order.final_price,
          },
        ];
    const totalBase = concepts.reduce((sum, concept) => sum + concept.base_amount, 0);
    const totalVat = concepts.reduce((sum, concept) => sum + concept.vat_amount, 0);
    const totalGross = concepts.reduce((sum, concept) => sum + concept.total_amount, 0);
    const shippedDate = order.shipped_at
      ? new Date(order.shipped_at).toLocaleDateString(locale)
      : '';
    const deliveredDate = order.delivered_at
      ? new Date(order.delivered_at).toLocaleDateString(locale)
      : '';
    const buyerNotesLine = order.buyer_notes
      ? `${select({ es: 'Notas del comprador', en: 'Buyer notes' })}: ${order.buyer_notes}`
      : '';
    const sellerNotesLine = order.seller_notes
      ? `${select({ es: 'Notas del vendedor', en: 'Seller notes' })}: ${order.seller_notes}`
      : '';

    return `
${select({ es: 'FACTURA DE VENTA - SOARTISTIC', en: 'SALES INVOICE - SOARTISTIC' })}
=====================================

${select({ es: 'NÚMERO DE FACTURA', en: 'INVOICE NUMBER' })}: ${order.order_number}
${select({ es: 'FECHA DE FACTURA', en: 'INVOICE DATE' })}: ${now.toLocaleDateString(locale)}
${select({ es: 'FECHA DE PEDIDO', en: 'ORDER DATE' })}: ${orderDate.toLocaleDateString(locale)}

${select({ es: 'VENDEDOR (ARTISTA)', en: 'SELLER (ARTIST)' })}
-------------------------------------
${select({ es: 'Nombre', en: 'Name' })}: ${user?.full_name || 'N/A'}
${select({ es: 'Usuario', en: 'Username' })}: @${user?.username || 'N/A'}
Email: ${user?.email || 'N/A'}
${paymentSettings?.business_name ? `${select({ es: 'Razón social', en: 'Business name' })}: ${paymentSettings.business_name}` : ''}
${paymentSettings?.tax_id ? `${select({ es: 'NIF / VAT ID', en: 'Tax ID / VAT ID' })}: ${paymentSettings.tax_id}` : ''}

${select({ es: 'COMPRADOR', en: 'BUYER' })}
-------------------------------------
${select({ es: 'Nombre', en: 'Name' })}: ${order.buyer_name || order.buyer_profile?.full_name || 'N/A'}
Email: ${order.buyer_email || 'N/A'}
${select({ es: 'Usuario', en: 'Username' })}: ${order.buyer_profile?.username ? '@' + order.buyer_profile.username : 'N/A'}

${select({ es: 'DETALLES DEL ARTÍCULO', en: 'ITEM DETAILS' })}
-------------------------------------
${select({ es: 'Obra', en: 'Artwork' })}: ${order.posts?.title || select({ es: 'Obra sin título', en: 'Untitled piece' })}
${select({ es: 'Descripción', en: 'Description' })}: ${order.posts?.description || select({ es: 'Sin descripción', en: 'No description' })}
${select({ es: 'Método de entrega', en: 'Delivery method' })}: ${getDeliveryMethodLabel(order.delivery_method)}

${select({ es: 'CONCEPTOS DE FACTURA', en: 'INVOICE ITEMS' })}
-------------------------------------
${concepts
  .map((concept) => {
    const vatLabel = concept.vat_rate
      ? `${concept.vat_rate}%`
      : select({ es: 'Exento', en: 'Exempt' });
    return `${getConceptTypeLabel(concept.type)}: ${concept.description}
${select({ es: 'Base imponible', en: 'Taxable base' })}: ${formatCurrency(concept.base_amount)}
${select({ es: 'Tipo IVA', en: 'VAT rate' })}: ${vatLabel}
${select({ es: 'Cuota IVA', en: 'VAT amount' })}: ${formatCurrency(concept.vat_amount)}
${select({ es: 'Importe total', en: 'Total amount' })}: ${formatCurrency(concept.total_amount)}`;
  })
  .join('\n\n')}

${select({ es: 'RESUMEN IMPOSITIVO', en: 'TAX SUMMARY' })}
-------------------------------------
${select({ es: 'Base imponible total', en: 'Total taxable base' })}: ${formatCurrency(totalBase)}
${select({ es: 'IVA total', en: 'Total VAT' })}: ${formatCurrency(totalVat)}
${select({ es: 'Importe total factura', en: 'Total invoice amount' })}: ${formatCurrency(totalGross)}
${select({ es: 'Régimen fiscal', en: 'Tax regime' })}: ${
  isIntracommunity
    ? select({ es: 'Operación intracomunitaria', en: 'Intra-Community transaction' })
    : select({ es: 'Operación nacional', en: 'Domestic transaction' })
}
${isIntracommunity ? `${select({ es: 'Estado VIES', en: 'VIES status' })}: ${
  viesVerified
    ? select({ es: 'Operador intracomunitario verificado', en: 'Verified intra-Community operator' })
    : select({ es: 'No verificado', en: 'Not verified' })
}` : ''}
${order.tax_exemption_reason ? `${select({ es: 'Motivo de exención', en: 'Exemption reason' })}: ${order.tax_exemption_reason}` : ''}

${select({ es: 'RESUMEN FINANCIERO', en: 'FINANCIAL SUMMARY' })}
-------------------------------------
${select({ es: 'Precio original', en: 'Original price' })}: ${formatCurrency(order.original_price)}
${select({ es: 'Precio final', en: 'Final price' })}: ${formatCurrency(order.final_price)}
${select({ es: 'Comisión', en: 'Commission' })} (${order.commission_rate}%): ${formatCurrency(order.commission_amount)}
${select({ es: 'Ganancias del artista', en: 'Artist earnings' })}: ${formatCurrency(order.artist_earnings)}

${select({ es: 'ESTADO DEL PEDIDO', en: 'ORDER STATUS' })}
-------------------------------------
${select({ es: 'Estado del pedido', en: 'Order status' })}: ${getStatusLabel(order.status)}
${select({ es: 'Estado del pago', en: 'Payment status' })}: ${getPaymentStatusLabel(order.payment_status)}
${order.tracking_number ? `${select({ es: 'Número de seguimiento', en: 'Tracking number' })}: ${order.tracking_number}` : ''}
${shippedDate ? `${select({ es: 'Enviado el', en: 'Shipped on' })}: ${shippedDate}` : ''}
${deliveredDate ? `${select({ es: 'Entregado el', en: 'Delivered on' })}: ${deliveredDate}` : ''}

${select({ es: 'NOTAS', en: 'NOTES' })}
-------------------------------------
${buyerNotesLine}
${sellerNotesLine}

${select({ es: 'INFORMACIÓN LEGAL', en: 'LEGAL INFORMATION' })}
-------------------------------------
${select({
  es: 'Esta factura ha sido generada automáticamente por SoArtistic.',
  en: 'This invoice was generated automatically by SoArtistic.',
})}
${select({
  es: 'Para cualquier consulta, contacte con soporte@soartistic.com',
  en: 'For any questions, contact soporte@soartistic.com',
})}
${isIntracommunity && viesVerified ? select({
  es: 'Operación intracomunitaria exenta de IVA. Inversión del sujeto pasivo según Directiva 2006/112/CE.',
  en: 'Intra-Community supply exempt from VAT. Reverse charge according to Directive 2006/112/EC.',
}) : ''}
${!isIntracommunity && effectiveVatRate
  ? select({
      es: `Tipo general de IVA aplicado: ${effectiveVatRate}%`,
      en: `Standard VAT rate applied: ${effectiveVatRate}%`,
    })
  : ''}
${paymentSettings?.payment_terms ? select({
  es: `Condiciones de pago: ${paymentSettings.payment_terms}`,
  en: `Payment terms: ${paymentSettings.payment_terms}`,
}) : ''}
${paymentSettings?.invoice_footer || ''}

${select({ es: 'Generado el', en: 'Generated on' })}: ${now.toLocaleString(locale)}
`;
  };

  const handleEditOrder = (order: Order) => {
    console.log('Editing order:', order);
    setSelectedOrder(order);
    setEditingOrder({
      buyer_email: order.buyer_email,
      buyer_name: order.buyer_name,
      final_price: order.final_price,
      status: order.status,
      payment_status: order.payment_status,
      seller_notes: order.seller_notes,
      tracking_number: order.tracking_number,
    });
    setShowEditModal(true);
  };

  const handleSaveOrder = async () => {
    if (!selectedOrder) return;
    
    setSaving(true);
    try {
      // Recalculate commission and earnings if price changed
      const newPrice = editingOrder.final_price || selectedOrder.final_price;
      const commissionAmount = newPrice * (selectedOrder.commission_rate / 100);
      const artistEarnings = newPrice - commissionAmount;

      const { error } = await supabase
        .from('orders')
        .update({
          buyer_email: editingOrder.buyer_email,
          buyer_name: editingOrder.buyer_name,
          final_price: newPrice,
          commission_amount: commissionAmount,
          artist_earnings: artistEarnings,
          status: editingOrder.status,
          payment_status: editingOrder.payment_status,
          seller_notes: editingOrder.seller_notes,
          tracking_number: editingOrder.tracking_number,
          updated_at: new Date().toISOString(),
          ...(editingOrder.status === 'shipped' && !selectedOrder.shipped_at && {
            shipped_at: new Date().toISOString()
          }),
          ...(editingOrder.status === 'delivered' && !selectedOrder.delivered_at && {
            delivered_at: new Date().toISOString()
          }),
        })
        .eq('id', selectedOrder.id)
        .eq('seller_id', user?.id);

      if (error) throw error;

      setShowEditModal(false);
      setEditingOrder({});
      setSelectedOrder(null);
      loadOrders(); // Reload to get updated data
    } catch (err) {
      console.error('Error updating order:', err);
      alert(select({ es: 'Error al actualizar el pedido', en: 'Error updating order' }));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOrder = (order: Order) => {
    console.log('Deleting order:', order);
    setSelectedOrder(order);
    setShowDeleteModal(true);
  };

  const confirmDeleteOrder = async () => {
    if (!selectedOrder) return;
    
    setDeleting(true);
    try {
      const { error } = await supabase
        .from('orders')
        .delete()
        .eq('id', selectedOrder.id)
        .eq('seller_id', user?.id);

      if (error) throw error;

      setShowDeleteModal(false);
      setSelectedOrder(null);
      loadOrders(); // Reload to remove deleted order
    } catch (err) {
      console.error('Error deleting order:', err);
      alert(select({ es: 'Error al eliminar el pedido', en: 'Error deleting order' }));
    } finally {
      setDeleting(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      processing: 'bg-orange-100 text-orange-800',
      shipped: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      refunded: 'bg-gray-100 text-gray-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-gray-200 h-32 rounded-xl"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Package className="text-purple-600" size={24} />
          {select({ es: 'Gestión de ventas', en: 'Sales management' })}
        </h1>
        <p className="text-gray-600 mt-1">
          {select({ es: 'Administra tus pedidos y facturas', en: 'Manage your orders and invoices' })}
        </p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg p-6 text-white">
          <div className="flex items-center gap-3">
            <Euro size={24} />
            <div>
              <p className="text-green-100">{select({ es: 'Ventas totales', en: 'Total sales' })}</p>
              <p className="text-2xl font-bold">
                {formatPrice(orders.reduce((sum, order) => sum + order.final_price, 0))}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg p-6 text-white">
          <div className="flex items-center gap-3">
            <Package size={24} />
            <div>
              <p className="text-blue-100">{select({ es: 'Pedidos totales', en: 'Total orders' })}</p>
              <p className="text-2xl font-bold">{orders.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg p-6 text-white">
          <div className="flex items-center gap-3">
            <TrendingUp size={24} />
            <div>
              <p className="text-purple-100">{select({ es: 'Ganancias netas', en: 'Net earnings' })}</p>
              <p className="text-2xl font-bold">
                {formatPrice(orders.reduce((sum, order) => sum + order.artist_earnings, 0))}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Orders List */}
      <div className="space-y-4">
        {orders.length > 0 ? (
          orders.map((order) => (
            <div key={order.id} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-4">
                  {order.posts?.media_files?.[0] && (
                    <img
                      src={order.posts.media_files[0].url}
                      alt={order.posts.title}
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                  )}
                  <div>
                    <h3 className="font-semibold text-gray-900 text-lg">
                      {order.posts?.title || select({ es: 'Obra sin título', en: 'Untitled piece' })}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {select({ es: 'Pedido', en: 'Order' })} #{order.order_number}
                    </p>
                    <p className="text-sm text-gray-500">
                      {select({ es: 'Comprador:', en: 'Buyer:' })}{' '}
                      {order.buyer_name || order.buyer_profile?.full_name || 'N/A'}
                    </p>
                    <p className="text-sm text-gray-500">{formatDate(order.created_at)}</p>
                  </div>
                </div>
                
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-900">{formatPrice(order.final_price)}</p>
                  <p className="text-sm text-gray-500">
                    {select({ es: 'Ganancias:', en: 'Earnings:' })} {formatPrice(order.artist_earnings)}
                  </p>
                  <div className="flex gap-2 mt-2">
                    <span className={`inline-block px-2 py-1 text-xs rounded-full ${getStatusColor(order.status)}`}>
                      {getStatusLabel(order.status)}
                    </span>
                    <span className={`inline-block px-2 py-1 text-xs rounded-full ${getStatusColor(order.payment_status)}`}>
                      {getPaymentStatusLabel(order.payment_status)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  <span>
                    {select({ es: 'Método:', en: 'Method:' })} {getDeliveryMethodLabel(order.delivery_method)}
                  </span>
                  <span className="ml-4">
                    {select({ es: 'Comisión:', en: 'Commission:' })} {formatPrice(order.commission_amount)}
                  </span>
                  {order.tracking_number && (
                    <span className="ml-4">
                      {select({ es: 'Seguimiento:', en: 'Tracking:' })} {order.tracking_number}
                    </span>
                  )}
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={() => handleViewOrderDetails(order)}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 transition-colors flex items-center gap-1"
                  >
                    <Eye size={14} />
                    {select({ es: 'Ver detalles', en: 'View details' })}
                  </button>
                  
                  <button
                    onClick={() => handleDownloadInvoice(order)}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 transition-colors flex items-center gap-1"
                    title={select({ es: 'Factura para el comprador', en: 'Invoice for buyer' })}
                  >
                    <Download size={14} />
                    {select({ es: 'Recibo', en: 'Receipt' })}
                  </button>

                  <button
                    onClick={async () => {
                      const downloadPdf = (base64: string, name: string) => {
                        try {
                          // Clean the base64 string
                          const cleanBase64 = base64.replace(/[^A-Za-z0-9+/=]/g, "");
                          
                          const byteCharacters = atob(cleanBase64);
                          const byteNumbers = new Array(byteCharacters.length);
                          for (let i = 0; i < byteCharacters.length; i++) {
                            byteNumbers[i] = byteCharacters.charCodeAt(i);
                          }
                          const byteArray = new Uint8Array(byteNumbers);
                          const blob = new Blob([byteArray], { type: 'application/pdf' });
                          
                          if (blob.size < 100) {
                              console.warn('Downloaded PDF blob is suspiciously small:', blob.size);
                              alert(select({ es: 'El archivo descargado parece estar vacío o dañado.', en: 'Downloaded file seems empty or corrupted.' }));
                              return;
                          }

                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = name;
                          document.body.appendChild(a);
                          a.click();
                          setTimeout(() => {
                            window.URL.revokeObjectURL(url);
                            document.body.removeChild(a);
                          }, 100);
                        } catch (e) {
                          console.error('Error downloading PDF:', e);
                          alert(select({ 
                            es: 'Error al procesar el archivo PDF.', 
                            en: 'Error processing PDF file.' 
                          }));
                          throw e;
                        }
                      };

                      try {
                        setLoading(true);
                        // Always call generateCommissionInvoice. 
                        // It handles idempotency (returns existing if present) and creation if missing.
                        const result = await paymentsService.generateCommissionInvoice(order.id);
                        
                        if (result?.pdfBase64) {
                           downloadPdf(result.pdfBase64, `factura-soartistic-${order.id}.pdf`);
                           
                           // Update local state if it was a new generation (though it should be auto now)
                           if (result?.invoiceId && !order.holded_invoice_id) {
                            setOrders(prevOrders => 
                              prevOrders.map(o => 
                                o.id === order.id ? { ...o, holded_invoice_id: result.invoiceId } : o
                              )
                            );
                          }
                        } else {
                           console.warn('No PDF content received');
                           alert(select({ es: 'No se pudo recuperar la factura.', en: 'Could not retrieve invoice.' }));
                        }
                      } catch (err) {
                        console.error(err);
                        alert(select({ es: 'Error al descargar la factura.', en: 'Error downloading invoice.' }));
                      } finally {
                        setLoading(false);
                      }
                    }}
                    className={`px-3 py-1 text-sm border rounded transition-colors flex items-center gap-1 ${
                      order.holded_invoice_id 
                        ? 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100' 
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                    title={select({ es: 'Descargar Factura de Comisión', en: 'Download Commission Invoice' })}
                  >
                    <Download size={14} />
                    {select({ es: 'Factura Comisión', en: 'Commission Invoice' })}
                  </button>
                  
                  <button
                    onClick={() => handleEditOrder(order)}
                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center gap-1"
                  >
                    <Edit size={14} />
                    {select({ es: 'Editar', en: 'Edit' })}
                  </button>
                  
                  <button
                    onClick={() => handleDeleteOrder(order)}
                    className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors flex items-center gap-1"
                  >
                    <Trash2 size={14} />
                    {select({ es: 'Eliminar', en: 'Delete' })}
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-12">
            <Package size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {select({ es: 'No hay pedidos aún', en: 'No orders yet' })}
            </h3>
            <p className="text-gray-500">
              {select({ es: 'Cuando vendas tu primera obra aparecerá aquí', en: 'Your first sale will appear here' })}
            </p>
          </div>
        )}
      </div>

      {/* Order Details Modal */}
      {showDetailsModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {select({ es: 'Detalles del pedido', en: 'Order details' })} #{selectedOrder.order_number}
                </h2>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  aria-label={select({ es: 'Cerrar', en: 'Close' })}
                >
                  <X size={20} />
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Order Info */}
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      {select({ es: 'Información del pedido', en: 'Order information' })}
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">{select({ es: 'Número:', en: 'Number:' })}</span>
                        <span className="font-medium">{selectedOrder.order_number}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{select({ es: 'Fecha:', en: 'Date:' })}</span>
                        <span className="font-medium">{formatDate(selectedOrder.created_at)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{select({ es: 'Estado:', en: 'Status:' })}</span>
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(selectedOrder.status)}`}>
                          {getStatusLabel(selectedOrder.status)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{select({ es: 'Estado del pago:', en: 'Payment status:' })}</span>
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(selectedOrder.payment_status)}`}>
                          {getPaymentStatusLabel(selectedOrder.payment_status)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{select({ es: 'Método de entrega:', en: 'Delivery method:' })}</span>
                        <span className="font-medium">{getDeliveryMethodLabel(selectedOrder.delivery_method)}</span>
                      </div>
                      {selectedOrder.tracking_number && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">{select({ es: 'Seguimiento:', en: 'Tracking:' })}</span>
                          <span className="font-medium">{selectedOrder.tracking_number}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      {select({ es: 'Información del comprador', en: 'Buyer information' })}
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <User size={16} className="text-gray-400" />
                        <span>{selectedOrder.buyer_name || selectedOrder.buyer_profile?.full_name || 'N/A'}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Mail size={16} className="text-gray-400" />
                        <span>{selectedOrder.buyer_email || 'N/A'}</span>
                      </div>
                      {selectedOrder.buyer_profile?.username && (
                        <div className="flex items-center gap-3">
                          <span className="text-gray-400">@</span>
                          <span>{selectedOrder.buyer_profile.username}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {(selectedOrder.buyer_notes || selectedOrder.seller_notes) && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        {select({ es: 'Notas', en: 'Notes' })}
                      </h3>
                      {selectedOrder.buyer_notes && (
                        <div className="mb-3">
                          <p className="text-sm font-medium text-gray-700">
                            {select({ es: 'Notas del comprador:', en: 'Buyer notes:' })}
                          </p>
                          <p className="text-gray-600">{selectedOrder.buyer_notes}</p>
                        </div>
                      )}
                      {selectedOrder.seller_notes && (
                        <div>
                          <p className="text-sm font-medium text-gray-700">
                            {select({ es: 'Notas del vendedor:', en: 'Seller notes:' })}
                          </p>
                          <p className="text-gray-600">{selectedOrder.seller_notes}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Product & Financial Info */}
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      {select({ es: 'Obra vendida', en: 'Artwork sold' })}
                    </h3>
                    <div className="border border-gray-200 rounded-lg p-4">
                      {selectedOrder.posts?.media_files?.[0] && (
                        <img
                          src={selectedOrder.posts.media_files[0].url}
                          alt={selectedOrder.posts.title}
                          className="w-full h-48 object-cover rounded-lg mb-3"
                        />
                      )}
                      <h4 className="font-semibold text-gray-900">
                        {selectedOrder.posts?.title || select({ es: 'Obra sin título', en: 'Untitled piece' })}
                      </h4>
                      <p className="text-gray-600 text-sm mt-1">
                        {selectedOrder.posts?.description || select({ es: 'Sin descripción', en: 'No description' })}
                      </p>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      {select({ es: 'Resumen financiero', en: 'Financial summary' })}
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">{select({ es: 'Precio original:', en: 'Original price:' })}</span>
                        <span className="font-medium">{formatPrice(selectedOrder.original_price)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{select({ es: 'Precio final:', en: 'Final price:' })}</span>
                        <span className="font-medium">{formatPrice(selectedOrder.final_price)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">
                          {select({ es: 'Comisión', en: 'Commission' })} ({selectedOrder.commission_rate}%):
                        </span>
                        <span className="font-medium text-red-600">-{formatPrice(selectedOrder.commission_amount)}</span>
                      </div>
                      <div className="border-t pt-3">
                        <div className="flex justify-between">
                          <span className="font-semibold text-gray-900">{select({ es: 'Ganancias netas:', en: 'Net earnings:' })}</span>
                          <span className="font-bold text-green-600 text-lg">{formatPrice(selectedOrder.artist_earnings)}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {selectedOrder.delivery_address && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        {select({ es: 'Dirección de entrega', en: 'Delivery address' })}
                      </h3>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <pre className="text-sm text-gray-700 whitespace-pre-wrap">
                          {JSON.stringify(selectedOrder.delivery_address, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-3 mt-8 pt-6 border-t">
                <button
                  onClick={() => handleDownloadInvoice(selectedOrder)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <Download size={16} />
                  {select({ es: 'Descargar factura', en: 'Download invoice' })}
                </button>
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    handleEditOrder(selectedOrder);
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                >
                  <Edit size={16} />
                  {select({ es: 'Editar pedido', en: 'Edit order' })}
                </button>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {select({ es: 'Cerrar', en: 'Close' })}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Order Modal */}
      {showEditModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {select({ es: 'Editar pedido', en: 'Edit order' })} #{selectedOrder.order_number}
                </h2>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  aria-label={select({ es: 'Cerrar', en: 'Close' })}
                >
                  <X size={20} />
                </button>
              </div>

              <form className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {select({ es: 'Email del comprador', en: 'Buyer email' })}
                    </label>
                    <input
                      type="email"
                      value={editingOrder.buyer_email || ''}
                      onChange={(e) => setEditingOrder({...editingOrder, buyer_email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {select({ es: 'Nombre del comprador', en: 'Buyer name' })}
                    </label>
                    <input
                      type="text"
                      value={editingOrder.buyer_name || ''}
                      onChange={(e) => setEditingOrder({...editingOrder, buyer_name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {select({ es: 'Precio final (EUR)', en: 'Final price (EUR)' })}
                    </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editingOrder.final_price || selectedOrder.final_price}
                    onChange={(e) => setEditingOrder({...editingOrder, final_price: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                    {editingOrder.final_price !== selectedOrder.final_price && (
                      <p className="text-sm text-amber-600 mt-1">
                        ⚠️ {select({
                          es: 'Cambiar el precio recalculará automáticamente las comisiones y ganancias',
                          en: 'Changing the price will automatically recalculate commissions and earnings',
                        })}
                      </p>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {select({ es: 'Estado del pedido', en: 'Order status' })}
                    </label>
                    <select
                      value={editingOrder.status || selectedOrder.status}
                      onChange={(e) => setEditingOrder({...editingOrder, status: e.target.value as Order['status']})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="pending">{getStatusLabel('pending')}</option>
                      <option value="confirmed">{getStatusLabel('confirmed')}</option>
                      <option value="processing">{getStatusLabel('processing')}</option>
                      <option value="shipped">{getStatusLabel('shipped')}</option>
                      <option value="delivered">{getStatusLabel('delivered')}</option>
                      <option value="cancelled">{getStatusLabel('cancelled')}</option>
                      <option value="refunded">{getStatusLabel('refunded')}</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {select({ es: 'Estado del pago', en: 'Payment status' })}
                    </label>
                    <select
                      value={editingOrder.payment_status || selectedOrder.payment_status}
                      onChange={(e) => setEditingOrder({...editingOrder, payment_status: e.target.value as Order['payment_status']})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="pending">{getPaymentStatusLabel('pending')}</option>
                      <option value="paid">{getPaymentStatusLabel('paid')}</option>
                      <option value="partially_paid">{getPaymentStatusLabel('partially_paid')}</option>
                      <option value="refunded">{getPaymentStatusLabel('refunded')}</option>
                      <option value="failed">{getPaymentStatusLabel('failed')}</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {select({ es: 'Número de seguimiento', en: 'Tracking number' })}
                  </label>
                  <input
                    type="text"
                    value={editingOrder.tracking_number || ''}
                    onChange={(e) => setEditingOrder({...editingOrder, tracking_number: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder={select({ es: 'Opcional', en: 'Optional' })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {select({ es: 'Notas del vendedor', en: 'Seller notes' })}
                  </label>
                  <textarea
                    rows={3}
                    value={editingOrder.seller_notes || ''}
                    onChange={(e) => setEditingOrder({...editingOrder, seller_notes: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                    placeholder={select({
                      es: 'Notas adicionales para el comprador...',
                      en: 'Additional notes for the buyer...',
                    })}
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    disabled={saving}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    {select({ es: 'Cancelar', en: 'Cancel' })}
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveOrder}
                    disabled={saving}
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                        {select({ es: 'Guardando...', en: 'Saving...' })}
                      </>
                    ) : (
                      <>
                        <Save size={16} />
                        {select({ es: 'Guardar cambios', en: 'Save changes' })}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  {select({ es: 'Confirmar eliminación', en: 'Confirm deletion' })}
                </h2>
                <button
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleting}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
                  aria-label={select({ es: 'Cerrar', en: 'Close' })}
                >
                  <X size={20} />
                </button>
              </div>

              <div className="mb-6">
                <p className="text-gray-700 mb-4">
                  {select({
                    es: `¿Estás seguro de que quieres eliminar el pedido #${selectedOrder.order_number}?`,
                    en: `Are you sure you want to delete order #${selectedOrder.order_number}?`,
                  })}
                </p>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-700">
                    ⚠️ {select({
                      es: 'Esta acción no se puede deshacer. Se eliminará permanentemente el pedido y toda su información.',
                      en: 'This action cannot be undone. The order and all related information will be permanently deleted.',
                    })}
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  {select({ es: 'Cancelar', en: 'Cancel' })}
                </button>
                <button
                  onClick={confirmDeleteOrder}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {deleting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      {select({ es: 'Eliminando...', en: 'Deleting...' })}
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} />
                      {select({ es: 'Eliminar pedido', en: 'Delete order' })}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesView;
